import type * as Fabric from '@postermywall/fabricjs-2';
import {CustomBorderTable, FixedLayout, LayoutManager, util} from '@postermywall/fabricjs-2';
import type {CopyableItemStyles} from '@PosterWhiteboard/items/item/item.types';
import {type InitItemOpts, ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {RGBA} from '@Utils/color.util';
import type {TableItemObject} from '@PosterWhiteboard/items/table-item/table-item.class';
import {TableItem} from '@PosterWhiteboard/items/table-item/table-item.class';
import {cacheIcon, loadedIcons} from '@PosterWhiteboard/items/menu-item/menu-icons';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import {prepareDataForMenuItem} from '@PosterWhiteboard/items/menu-item/user-menu';
import type {MenuItemData} from '@PosterWhiteboard/items/menu-item/menu-item.types';
import {BorderType} from '@PosterWhiteboard/classes/item-border.class';
import {LayoutTypes} from '@PosterWhiteboard/items/layouts/layout.types';
import type {MenuData} from '@Libraries/add-media-library';
import {ElementDataType} from '@Libraries/add-media-library';
import editIconSVG from '@PosterWhiteboard/poster/poster-item-control-svgs/edit-icon.svg';
import type {LoadSVGDataResponse} from '@Utils/fabric.util';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import type {DeepPartial} from '@/global';

export const DEFAULT_FONT_FAMILY = 'OpenSansRegular';
export const DEFAULT_FONT_FAMILY2 = 'Abel';
export const MENU_LAYOUTS = [
  LayoutTypes.MENU_LAYOUT_1,
  LayoutTypes.MENU_LAYOUT_2,
  LayoutTypes.MENU_LAYOUT_3,
  LayoutTypes.MENU_LAYOUT_4,
  LayoutTypes.MENU_LAYOUT_5,
  LayoutTypes.MENU_LAYOUT_6,
];

export interface MenuItemObject extends TableItemObject {
  itemIds: string[];
  copiedItemIds: string[];
  iconsColor: RGBA;
  iconsSize: number;
  wrappingInfo: string[][] | null;
  // totalIconsToLoad: number;
  // numIconsLoaded: number;
}

export class MenuItem extends TableItem {
  declare _fabricObject: Fabric.CustomBorderTable;
  public override gitype = ITEM_TYPE.MENU;
  public itemIds: string[] = [];
  public copiedItemIds: string[] = [];
  public iconsColor: RGBA = [0, 0, 0, 1];
  public iconsSize = 0;
  public wrappingInfo: string[][] | null = null;
  /**
   * The width the menu had in the editor when it was saved. Its layout grows the menu to fit its rows, so a
   * high-res render, whose rows are built differently, needs this to lay the rows out as wide as the user saw them.
   */
  public savedWidth: number | null = null;
  public totalIconsToLoad = 0;
  public numIconsLoaded = 0;

  override get fabricObject(): Fabric.CustomBorderTable {
    return this._fabricObject;
  }

  protected override async getFabricObjectForItem(opts: InitItemOpts = {}): Promise<CustomBorderTable> {
    this.savedWidth = opts.width ?? null;
    return new Promise((resolve) => {
      resolve(
        new CustomBorderTable([], {
          objectCaching: false,
          perPixelTargetFind: true,
          width: opts?.width ?? 300,
          height: opts?.height ?? 200,
          scaleX: opts?.scaleX ?? 1,
          scaleY: opts?.scaleY ?? 1,
          pmwBmBtnText: window.i18next.t('pmwjs_edit_item'),
          pmwBmBtnIcon: editIconSVG as string,
          layoutManager: new LayoutManager(new FixedLayout()),
        })
      );
    });
  }

  public override editItem(): void {
    trackPosterBuilderGA4Events(GA4EventName.MENU_MAKER_FROM_CANVAS_EDIT_BUTTON);
    getPosterUi().openEditMenuModal(this);
  }

  protected override onItemDoubleClicked(): void {
    trackPosterBuilderGA4Events(GA4EventName.MENU_MAKER_FROM_CANVAS_EDIT_BUTTON);
    getPosterUi().openEditMenuModal(this);
  }

  public updateFromMenuData(updatedSelectedItems: MenuItemData[], layout: LayoutTypes): void {
    const changes = prepareDataForMenuItem(updatedSelectedItems, layout);
    changes.border = {
      ...this.border,
      solidBorderType: layout === LayoutTypes.MENU_LAYOUT_4 && !this.border.solidBorderType ? BorderType.RECTANGLE_BORDER : this.border.solidBorderType,
    };

    if (changes.itemIds?.length === 0) {
      this.page.poster.deleteItemById(this.uid);
    } else {
      void this.updateFromObject(changes);
    }
  }

  public override getCopyableStyles(): CopyableItemStyles {
    return {
      ...super.getCopyableStyles(),
      iconsColor: this.iconsColor,
      iconsSize: this.iconsSize,
    };
  }
  public override async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    const stylesObj: DeepPartial<MenuItemObject> = {};
    if (copiedStyles.iconsSize !== undefined) {
      stylesObj.iconsSize = copiedStyles.iconsSize;
    }
    if (copiedStyles.iconsColor !== undefined) {
      stylesObj.iconsColor = copiedStyles.iconsColor;
    }

    await this.updateFromObject(stylesObj, {undoable: false});
    await super.pasteStyles(copiedStyles);
  }

  protected override async updateFabricObject(): Promise<void> {
    await super.updateFabricObject();
    this.invalidateIcons();
  }

  /**
   * Invalidates list of required icons for this menu graphic item.
   */
  invalidateIcons(): void {
    let i;
    const icons = this.layoutDataMap.iconsList;
    let iconsToLoad: string[] = [];
    if (icons) {
      for (i = 0; i < icons.length; i++) {
        const iconsList = icons[i].getValue() as string[];
        for (let j = 0; j < iconsList.length; j++) {
          if (!(iconsList[j] in loadedIcons)) {
            iconsToLoad.push(iconsList[j]);
          }
        }
      }
    }

    // removing duplicates from iconsToLoad array.
    iconsToLoad = iconsToLoad.filter((value, index, self) => {
      return self.indexOf(value) === index;
    });

    this.totalIconsToLoad = iconsToLoad.length;
    this.numIconsLoaded = 0;

    for (i = 0; i < iconsToLoad.length; i++) {
      cacheIcon(iconsToLoad[i])
        .then(this.onIconLoaded.bind(this, iconsToLoad[i]))
        .catch((e) => {
          console.error(e);
        });
    }
  }

  /**
   * Callback function to use when icon's svg file is loaded from the server
   * @private
   */
  onIconLoaded(name: string, svgData: LoadSVGDataResponse): void {
    // group svg elements and cache the fabric.Path object representing the icon
    this.numIconsLoaded += 1;
    if (svgData.paths) {
      loadedIcons[name] = util.groupSVGElements(svgData.paths, {
        ...svgData.metaData,
        selectable: false,
        scaleX: 1,
        scaleY: 1,
        left: -svgData.metaData.width / 2,
        top: -svgData.metaData.height / 2,
      });
    }
    if (this.totalIconsToLoad === this.numIconsLoaded) {
      this.updateFabricObject().catch(() => {});
      this.fabricObject.setCoords();
    }
    this.page.fabricCanvas.requestRenderAll();
  }

  /**
   * Returns an array of arrays containing wrapped lines of text by the view.
   * @returns {Array} Can be null if no data is present.
   */
  getWrappedLines(): string[][] {
    return this.layout.getWrappingInfo();
  }

  public override toObject(): MenuItemObject {
    return {
      ...super.toObject(),
      itemIds: this.itemIds,
      copiedItemIds: this.copiedItemIds,
      iconsColor: this.iconsColor,
      iconsSize: this.iconsSize,
      wrappingInfo: this.wrappingInfo,
    };
  }

  updateCopiedItems(items: string[]): void {
    this.copiedItemIds = items;
  }

  public getChangesOnMenuLayoutChange(layout: LayoutTypes): Partial<MenuItemObject> {
    return {
      layoutStyle: layout,
      border: {
        ...this.border,
        solidBorderType: layout === LayoutTypes.MENU_LAYOUT_4 ? BorderType.RECTANGLE_BORDER : BorderType.NONE,
      },
    };
  }

  public updateMenuLayout(layout: LayoutTypes): void {
    void this.updateFromObject(this.getChangesOnMenuLayoutChange(layout));
  }

  public updateIconsColor(iconsColor: RGBA, undoable = true): void {
    void this.updateFromObject(
      {
        iconsColor,
      },
      {
        undoable,
      }
    );
  }

  protected override onItemDoubleTapped(): void {
    trackPosterBuilderGA4Events(GA4EventName.MENU_MAKER_FROM_CANVAS_EDIT_BUTTON);
    getPosterUi().openEditMenuModal(this);
  }
}

export const addMenuToPoster = (menuItems: MenuItemData[], layout: string): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }
  trackPosterBuilderGA4Events(GA4EventName.ADDED_MENU, {[GA4EventParamName.TYPE]: layout});
  void currentPage.items.addItems.addMenuItem(
    {
      type: ElementDataType.MENU,
      ...prepareDataForMenuItem(menuItems, layout),
    } as MenuData,
    {},
    true
  );
};
